import type { z } from 'zod';

import { PermissionGroupType } from '../permissionGroups/enums';
import { getPermissionGroupType } from '../permissionGroups/schemas';
import { PermissionSource, PermissionSourceType } from '../permissions/enums';
import { readPermissionTag } from '../permissions/tags';
import type {
  DBUserPermissionSourceSchema,
  UserPermissionGenericPermissionsSchema,
  UserPermissionSourceSchema,
} from './schemas';

type Source = z.infer<typeof UserPermissionSourceSchema>;
type DBSource = z.infer<typeof DBUserPermissionSourceSchema>;
type Entry = z.infer<typeof UserPermissionGenericPermissionsSchema>[number];

/** A permission shared with the user themselves. */
export const DIRECT_PERMISSION_SOURCE: Source = { type: PermissionSourceType.Direct };

/** A permission the user holds through a group they are in. */
export const getGroupPermissionSource = (permissionGroupId: string): Source => ({
  type:
    getPermissionGroupType(permissionGroupId) === PermissionGroupType.Class
      ? PermissionSourceType.Class
      : PermissionSourceType.Custom,
  permissionGroupId,
});

export const isSamePermissionSource = (a: Source, b: Source) =>
  a.type === PermissionSourceType.Direct || b.type === PermissionSourceType.Direct
    ? a.type === b.type
    : a.permissionGroupId === b.permissionGroupId;

/** A source as stored: `DIRECT`, or the id of the group that grants it. */
export const encodePermissionSource = (source: Source): DBSource =>
  source.type === PermissionSourceType.Direct ? PermissionSource.Direct : source.permissionGroupId;

export const decodePermissionSource = (source: DBSource): Source =>
  source === PermissionSource.Direct ? DIRECT_PERMISSION_SOURCE : getGroupPermissionSource(source);

/** A user's hold on a permission, with what the permission grants. */
export const toUserPermissionEntry = (permissionId: string, sources: Source[]): Entry => ({
  permissionId,
  ...readPermissionTag(permissionId),
  sources,
});
